import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/router";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { WinShopLogoBeta, WSButton, WSSpinner } from "@winshop-crm/ui";

import { FormInput } from "../Detail/fields";
import { SignInForm, signInSchema } from "./signIn.schema";

const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const error = router.query.error ? String(router.query.error) : "";

  const { control, handleSubmit } = useForm<SignInForm>({
    resolver: zodResolver(signInSchema),
    mode: "onTouched",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = handleSubmit((values) => {
    setIsLoading(true);
    let callbackUrl = `${window.location.origin}/events`;
    if (router?.query?.callbackUrl) {
      callbackUrl = String(router.query.callbackUrl);
    }
    signIn("credentials", {
      ...values,
      userAgent: navigator.userAgent,
      callbackUrl,
    });
  });

  return (
    <div className="bg-primary-backgroundSubtle">
      <div className="flex flex-col justify-center w-screen min-h-screen py-12 m-auto max-w-3/4 md:max-w-1/2 lg:max-w-1/3 sm:px-6 lg:px-8">
        <div className="w-full sm:mx-auto">
          <WinShopLogoBeta className="w-auto h-20 mx-auto" />
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="px-4 py-8 bg-white shadow-sm sm:rounded-lg sm:px-10">
            <h2 className="mb-6 text-3xl font-extrabold text-center text-neutral-text">
              Přihlaste se
            </h2>
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              <FormInput
                control={control}
                name="email"
                label="Email adresa"
                type="email"
                autoComplete="email"
              />
              <FormInput
                control={control}
                name="password"
                label="Heslo"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                endAdornment={
                  <button
                    type="button"
                    aria-label={showPassword ? "Skrýt heslo" : "Zobrazit heslo"}
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="size-5" aria-hidden="true" />
                    ) : (
                      <Eye className="size-5" aria-hidden="true" />
                    )}
                  </button>
                }
              />
              {error && (
                <div>
                  <p className="block text-sm font-medium text-danger-text">
                    Špatný e-mail nebo heslo.
                  </p>
                </div>
              )}
              <WSButton
                type="submit"
                variant="full"
                color="primary"
                startIcon={isLoading && <WSSpinner />}
                disabled={isLoading}
              >
                Přihlásit
              </WSButton>
            </form>

            <div className="mt-6">
              <div className="mt-6">
                <div>
                  <a
                    href="https://www.pokladny.com/kontakty.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Nemáte účet? Požádejte o něj"
                    className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium bg-neutral-background "
                  >
                    <p className="tracking-tight text-neutral-text ">
                      <span className="block xl:inline">Nemáte účet? </span>
                      <span className="block text-primary-text xl:inline">Požádejte o něj</span>
                    </p>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
